/**
 * Channel routing policy for the shared application logger.
 *
 * These helpers answer *whether* a channel is allowed to deliver in the current
 * environment. They are deliberately independent from log severity: severity
 * decides how important a log is, policy decides where it may go.
 */

/**
 * Whether the app is running as a local development build (Metro / bundler run),
 * as opposed to any deployed environment.
 *
 * Anchored to `__DEV__` on purpose: it is `false` in every deployed build,
 * including the deployed `dev`/staging environment whose `env()` returns
 * `RBIEnv.DEV`. Keying off `env()` would misclassify deployed dev as local and
 * silently disable Datadog there.
 */
export const isLocalDevelopment = (): boolean => __DEV__ === true;

/**
 * Explicit opt-in for Datadog log delivery during local development.
 *
 * Set `EXPO_PUBLIC_ENABLE_LOCAL_DATADOG_LOGS=true` before starting the app
 * locally when you need to inspect logs in Datadog. Read at call time so it can
 * be toggled for a dev session (or a test) without changing app call sites.
 */
export const isLocalDatadogLogDeliveryEnabled = (): boolean =>
  process.env.EXPO_PUBLIC_ENABLE_LOCAL_DATADOG_LOGS === 'true';

/**
 * Datadog channel policy. Fail-safe direction: send in any non-local
 * (production-like) environment, and only send from local development when the
 * developer has explicitly opted in.
 */
export const isDatadogChannelEnabled = (): boolean =>
  !isLocalDevelopment() || isLocalDatadogLogDeliveryEnabled();
